import {
  Feedback,
  CreateFeedbackRequest,
  FeedbackStatus,
  CategoryAssignee
} from '../types/models';
import { supabase } from './supabase';


/**
 * 피드백 관련 서비스
 */

/**
 * 새로운 피드백 생성
 */
export const createFeedback = async (
  feedbackData: CreateFeedbackRequest, 
  user: { id: string; name: string; email: string }
): Promise<Feedback> => {
  try {
    // 0. 현재 세션 확인
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session) {
      throw new Error('사용자 인증이 필요합니다.');
    }
    
    const authenticatedUserId = sessionData.session.user.id;
    const authenticatedUserEmail = sessionData.session.user.email;

    // 1. 첨부파일 업로드 처리
    let attachmentUrls: string[] = [];
    if (feedbackData.attachments && feedbackData.attachments.length > 0) {
      attachmentUrls = await uploadFeedbackAttachments(
        feedbackData.attachments, 
        authenticatedUserEmail || user.email,
        feedbackData.subject
      );
    }

    // 2. 카테고리별 담당자 조회
    const categoryAssignees = await getAllCategoryAssignees();
    const assignee = categoryAssignees.find(ca => ca.category === feedbackData.category);

    // 3. 피드백 데이터 생성 (실제 인증된 사용자 정보 사용)
    const feedbackToInsert = {
      sender_id: authenticatedUserId,
      sender_name: user.name || sessionData.session.user.user_metadata?.name || '(정보 없음)',
      sender_email: authenticatedUserEmail || user.email,
      category: feedbackData.category,
      subject: feedbackData.subject,
      content: feedbackData.content,
      attachments: attachmentUrls.length > 0 ? attachmentUrls : [],
      status: FeedbackStatus.PENDING,
      assigned_to: assignee?.assignee_email || null,
    };


    // 4. 데이터베이스에 삽입 (세션 토큰 명시적 설정)
    const { data, error } = await supabase
      .from('feedback')
      .insert(feedbackToInsert)
      .select()
      .single();

    if (error) {
      console.error('Error creating feedback:', error);
      throw error;
    }

    // 5. 데이터 변환하여 반환
    const feedback: Feedback = {
      ...data,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at),
      responded_at: data.responded_at ? new Date(data.responded_at) : undefined,
    };

    console.log('✅ 피드백 생성 성공:', {
      id: feedback.id,
      category: feedback.category,
      subject: feedback.subject,
      sender: feedback.sender_name,
      attachments: feedback.attachments?.length || 0
    });

    return feedback;

  } catch (error) {
    console.error('❌ 피드백 생성 실패:', error);
    throw error;
  }
};

/**
 * 모든 카테고리 담당자 조회
 */
export const getAllCategoryAssignees = async (): Promise<CategoryAssignee[]> => {
  try {
    const { data, error } = await supabase
      .from('feedback_category_assignees')
      .select('*')
      .order('category');

    if (error) {
      console.error('Error fetching all category assignees:', error);
      throw error;
    }

    console.log(`카테고리 담당자 정보 로드 성공: ${data?.length || 0}개`);
    return data || [];

  } catch (error) {
    console.error('❌ 카테고리 담당자 조회 실패:', error);
    throw error;
  }
};

/**
 * 파일명에 사용할 수 없는 문자 제거
 */
const sanitizeFileName = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9\-_.]/g, '') // 한글과 특수문자 모두 제거 (영문+숫자만 허용)
    .replace(/\s+/g, '_') // 공백을 언더스코어로 변경
    .slice(0, 20); // 최대 20자로 제한
};

/**
 * 이메일을 파일 경로용으로 변환
 */
const sanitizeEmail = (email: string): string => {
  return email.replace('@', '_').replace(/\./g, '_');
};

/**
 * 피드백 첨부파일 업로드
 */
export const uploadFeedbackAttachments = async (
  files: File[], 
  userEmail: string, 
  subject: string
): Promise<string[]> => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD 형식
    const sanitizedSubject = sanitizeFileName(subject);
    const sanitizedEmail = sanitizeEmail(userEmail);
    
    const uploadPromises = files.map(async (file, index) => {
      // 파일명 생성: sanitizedEmail/date_subject_index_originalName
      const sanitizedOriginalName = sanitizeFileName(file.name);
      const fileName = `${sanitizedEmail}/${today}_${sanitizedSubject}_${index + 1}_${sanitizedOriginalName}`;
      
      // 파일 업로드 (feedbacks 버킷 사용)
      const { data, error } = await supabase.storage
        .from('feedbacks')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        throw error;
      }

      // 업로드된 파일의 공개 URL 생성
      const { data: publicURL } = supabase.storage
        .from('feedbacks')
        .getPublicUrl(data.path);

      return publicURL.publicUrl;
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    console.log(`첨부파일 업로드 성공: ${uploadedUrls.length}개 파일 업로드 완료`);
    return uploadedUrls;

  } catch (error) {
    console.error('첨부파일 업로드 실패:', error);
    throw error;
  }
};

